import { api } from "./api";
import { db, type SyncQueueItem } from "./db";

export interface SyncFlushResult {
  synced: number;
  failed: number;
  unauthorized: boolean;
  pendingRemaining: number;
}

export interface SyncErrorDetails {
  itemId: string;
  operation: string;
  endpoint: string;
  statusCode?: number;
  message: string;
}

function getActiveContext(tenantId?: string, storeId?: string): { tenantId: string; storeId: string } {
  if (tenantId && storeId) {
    return { tenantId, storeId };
  }
  try {
    const raw = localStorage.getItem("bledger.session");
    if (raw) {
      const s = JSON.parse(raw);
      return {
        tenantId: tenantId || s?.tenant?.id || "default",
        storeId: storeId || s?.store?.id || "default",
      };
    }
  } catch {}
  return {
    tenantId: tenantId || "default",
    storeId: storeId || "default",
  };
}

/**
 * P0 — SYNC STATE MACHINE: App restart recovery.
 * Recovers commands stuck in SYNCING state by resetting them to PENDING.
 */
export async function recoverStuckSyncing(tenantId?: string, storeId?: string): Promise<number> {
  const context = getActiveContext(tenantId, storeId);
  const stuckItems = await db.syncQueue
    .where("status")
    .equals("SYNCING")
    .toArray();

  const matching = stuckItems.filter(
    (item) => item.tenantId === context.tenantId && item.storeId === context.storeId,
  );

  for (const item of matching) {
    await db.syncQueue.update(item.id, {
      status: "PENDING",
      error: undefined,
      lastError: undefined,
    });
  }

  return matching.length;
}

/**
 * Queues an outbox command.
 */
export async function queueCommand(
  type: SyncQueueItem["type"],
  endpoint: string,
  payload: unknown,
  options?: {
    tenantId?: string;
    storeId?: string;
    idempotencyKey?: string;
  },
): Promise<string> {
  const context = getActiveContext(options?.tenantId, options?.storeId);
  const id = options?.idempotencyKey || crypto.randomUUID();

  await db.syncQueue.put({
    id,
    tenantId: context.tenantId,
    storeId: context.storeId,
    operation: type || "UNKNOWN",
    type,
    endpoint,
    payload,
    idempotencyKey: id,
    status: "PENDING",
    attemptCount: 0,
    createdAt: new Date().toISOString(),
  });

  return id;
}

/**
 * P0 — SYNC STATE MACHINE & TENANT ISOLATION:
 * Flushes only the commands belonging to the authenticated tenant and store.
 */
export async function flushSyncQueue(
  tenantId?: string,
  storeId?: string,
): Promise<SyncFlushResult> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const pendingCount = await getPendingSyncCount(tenantId, storeId);
    return { synced: 0, failed: 0, unauthorized: false, pendingRemaining: pendingCount };
  }

  const context = getActiveContext(tenantId, storeId);

  // 1. Recover any stuck SYNCING commands for this tenant/store
  await recoverStuckSyncing(context.tenantId, context.storeId);

  // 2. Fetch candidates belonging strictly to current tenant/store
  const items = await db.syncQueue
    .where("status")
    .anyOf("PENDING", "FAILED")
    .sortBy("createdAt");

  const tenantItems = items.filter(
    (item) => item.tenantId === context.tenantId && item.storeId === context.storeId,
  );

  let synced = 0;
  let failed = 0;
  let unauthorized = false;
  const now = Date.now();

  for (const item of tenantItems) {
    // Bounded exponential backoff on retry (1s, 2s, 4s, 8s, up to 30s)
    if (item.status === "FAILED" && item.lastAttemptAt) {
      const attempts = item.attemptCount || 1;
      const backoffMs = Math.min(1000 * Math.pow(2, attempts - 1), 30000);
      const timeSinceLast = now - new Date(item.lastAttemptAt).getTime();
      if (timeSinceLast < backoffMs) {
        // Still within backoff window
        continue;
      }
    }

    // Transition: PENDING / FAILED -> SYNCING
    await db.syncQueue.update(item.id, {
      status: "SYNCING",
      lastAttemptAt: new Date().toISOString(),
      error: undefined,
      lastError: undefined,
    });

    try {
      await api(item.endpoint, {
        method: "POST",
        body: JSON.stringify(item.payload),
        headers: {
          "idempotency-key": item.idempotencyKey || item.id,
        },
      });

      // Transition: SYNCING -> SYNCED
      await db.syncQueue.update(item.id, {
        status: "SYNCED",
        lastError: undefined,
        error: undefined,
      });

      synced += 1;
    } catch (error) {
      const status = (error as { status?: number }).status;
      if (status === 401) {
        unauthorized = true;
      }

      const errorMessage = error instanceof Error ? error.message : "Lỗi đồng bộ";
      const nextAttemptCount = (item.attemptCount || 0) + 1;

      // Transition: SYNCING -> FAILED (Retryable)
      await db.syncQueue.update(item.id, {
        status: "FAILED",
        attemptCount: nextAttemptCount,
        lastAttemptAt: new Date().toISOString(),
        error: errorMessage,
        lastError: errorMessage,
      });

      failed += 1;
      if (unauthorized) break;
    }
  }

  const pendingRemaining = await getPendingSyncCount(context.tenantId, context.storeId);
  return { synced, failed, unauthorized, pendingRemaining };
}

/**
 * Returns number of pending or syncing items for the active tenant/store.
 */
export async function getPendingSyncCount(tenantId?: string, storeId?: string): Promise<number> {
  const context = getActiveContext(tenantId, storeId);
  const items = await db.syncQueue
    .where("status")
    .anyOf("PENDING", "SYNCING", "FAILED")
    .toArray();

  return items.filter(
    (item) => item.tenantId === context.tenantId && item.storeId === context.storeId,
  ).length;
}

/**
 * Manual sync trigger for the user.
 */
export async function syncNow(tenantId?: string, storeId?: string): Promise<SyncFlushResult> {
  return flushSyncQueue(tenantId, storeId);
}

// Online event trigger: automatically trigger flush when network is restored
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    void flushSyncQueue().catch((err) => {
      console.warn("Background auto-sync on network restore failed:", err);
    });
  });
}
